import { Cesium3DTileStyle } from 'cesium';
import axiosRetry from 'axios-retry';
import centroid from '@turf/centroid';
import { getGeom } from '@turf/invariant';
import osmtogeojson from 'osmtogeojson/osmtogeojson.js';
import { createDatasetFromPlainDataset } from '../../../classes/Dataset';


const VIEWER_STATE = Object.freeze({
	/** The user can freely navigate the globe and select objects by clicking on them */
	DEFAULT: 'DEFAULT',
	/** Shape authoring workflow allowing the user to author a shape */
	SAW: 'SAW',
	/** Whenever a user clicks a "place" event is fired */
	PLACEMENT: 'PLACEMENT',
	/** OSM selection workflow for saving locations/camera views */
	OSM_SELECT: 'OSM_SELECT',
	ZOOM: 'ZOOM',
});

export function enterOsmWorkflow() {
	if (this.state !== VIEWER_STATE.DEFAULT)
		throw new Error(
			'Cannot enter OSM selection workflow from current state'
		);
	this.state = VIEWER_STATE.OSM_SELECT;
}

export function postOsmStyle() {
	const newData = this.osmWorkflow.addedIds.find(
		(osmBuilding) => osmBuilding.elementId === this.osmWorkflow.focusedId
	);
	this.updateEntityData(this.osmWorkflow.focusedEntity, {
		data: newData,
	});
}

export function toggleOsmSelectionMode() {
	const cesiumViewer = this.getCesiumData().viewer;
	try {
		this.enterOsmWorkflow();
		cesiumViewer.imageryLayers.get(0).brightness = 0.4;
		this.brightness = 0.4;
		cesiumViewer.scene.requestRender();
	} catch {
		cesiumViewer.imageryLayers.get(0).brightness = 0.9;
		this.brightness = 0.9;
		this.osmWorkflow.selectedIds = [];
		this.updateOsmStyle();
		cesiumViewer.scene.requestRender();
		this.state = VIEWER_STATE.DEFAULT;
	}
}

export function toggleOsmEditMode(value) {
	this.osmWorkflow.editMode = value;
}

export async function getOsmGeoJson(featuretype, id) {
	axiosRetry(this.axios, { retries: 3 });

	const osm = await this.axios
		.get(
			`https://www.openstreetmap.org/api/0.6/${featuretype}/${id}/full.json`
		)
		.then((response) => response.data)
		.catch((error) => {
			if (error.response) {
				/*  */
			}
			return null;
		});
	if (osm) {
		return osmtogeojson(osm);
	}
	return null; // API returns no data when a feature is unavailable. 410 Gone
}

export async function addAllSelectedOsmBuildings() {
	document.body.style.cursor = 'wait';

	this.osmWorkflow.selectedIds.map(this.addOsmBuildingAsEntity);
}
export async function addOsmBuildingAsEntity(info) {
	const alreadyAddedOsmBuilding = this.osmWorkflow.addedIds.find(
		(x) => x.elementId === info.elementId
	);
	if (!alreadyAddedOsmBuilding) {
		info.show = true;
		info.color = "color('#d5d5ff', 1)";
		if (!info.name) {
			if (info['addr:housenumber'] && info['addr:street']) {
				info.name = `${info['addr:housenumber']} ${info['addr:street']}`;
			} else {
				info.name = String(info.elementId);
			}
		}
		const footprint = await this.getOsmGeoJson(
			info.elementType,
			info.elementId
		);
		info.geoJson = footprint;

		const buildingDataset = createDatasetFromPlainDataset({
			type: 'generic',
			basedata: true,
			subType: null,
		});

		buildingDataset.data.properties = info;


		const osmBuildingLayer = {
			type: 'layer',
			name: `${info.name}`,
			geometry: getGeom(
				centroid(info.geoJson.features[0].geometry)
			),
			tags: ['3d model', 'osm'],
			parent: null,
			data: [
				{
					type: 'generic',
					basedata: true,
					subType: null,
					data: buildingDataset.data,
				},
			],
		};
		const postEntityLayerData = await this.axios.post(
			`/scenarios/${this.$store.state.scenario}/entities`,
			osmBuildingLayer
		);
		const assetProfileInfo = {
			name: info.name,
			latitude: info['cesium#latitude'],
			longitude: info['cesium#longitude'],
			city: info['addr:city'],
			state: info['addr:state'],
			postalCode: info['addr:postcode'],
			country: info['addr:country'],
		};

		await this.createAssetDataset(postEntityLayerData.data.id, assetProfileInfo)
		
		// @TODO: FIX SO SET VISIBILITY WORKS WITH NEW DATASET TYPE
		const osmBuildingModel = {
			type: 'osmBuildingModel',
			name: `${info.name} Model`,
			description: `${info.name} Model`,
			geometry: getGeom(
				centroid(info.geoJson.features[0].geometry)
			),
			tags: ['3d model', 'osm'],
			parent: postEntityLayerData.data.id,
			data: [
				{
					type: 'generic',
					basedata: true,
					subType: null,
					data: info,
				},
			],
		};

		const postEntityModelData = await this.axios.post(
			`/scenarios/${this.$store.state.scenario}/entities`,
			osmBuildingModel
		);

		postEntityModelData.data.data[0].data.entityId =
			postEntityModelData.data.id;

		const osmBuildingFootprint = {
			type: 'osmBuildingFootprint',
			name: `${info.name} Footprint`,
			description: `${info.name} Footprint`,
			geometry: info.geoJson.features[0].geometry,
			tags: ['3d model', 'osm'],
			parent: postEntityLayerData.data.id,
			data: [
				{
					type: 'generic',
					basedata: true,
					subType: null,
					data: info,
				},
			],
		};

		const postEntityFootprintData = await this.axios.post(
			`/scenarios/${this.$store.state.scenario}/entities`,
			osmBuildingFootprint
		);

		await this.addScenarioData([
			postEntityLayerData.data,
			postEntityModelData.data,
			postEntityFootprintData.data,
		]);
		this.osmWorkflow.selectedIds = [];
		this.updateOsmStyle();
	} else {
		/* console.log('Already have this entity so do something.'); */
	}
}

export function updateOsmStyle() {
	const cesiumViewer = this.getCesiumData().viewer;
	this.osmBuildings.style = new Cesium3DTileStyle(
		this.osmBuildingsStyleConditions
	);
	cesiumViewer.scene.requestRender();
}

export async function colorOsmBuildingById(color) {
	const id = this.osmWorkflow.focusedId;
	const { r, g, b, a } = color.rgba;
	const colorString = `rgba(${r},${g},${b},${a})`;
	const alreadyAdded = this.osmWorkflow.addedIds.find(
		(osmBuilding) => osmBuilding.elementId === id
	);
	if (alreadyAdded) {
		alreadyAdded.color = colorString;
	} else if (!this.osmWorkflow.styledIds[id]) {
		this.osmWorkflow.styledIds[id] = {
			show: true,
			color: colorString,
		};
	} else {
		this.osmWorkflow.styledIds[id].color = colorString;
	}
	this.updateOsmStyle();
}

export function resetOsmBuildingStyle() {
	this.osmWorkflow.selectedIds = [];
	// this.osmWorkflow.colorConditions = [['true', "color('white', 0.4)"]];
	this.osmBuildings.style = new Cesium3DTileStyle({
		color: "color('#d5d5ff', 1)",
	});
	this.osmWorkflow.showOnlySelected = false;
	this.getTreeMenu();
}
